import { VehicleType } from '@thive/fddm-types';

import { logger } from '@/modules/common/logging/logger';

import {
  FileReferenceResponse,
  UnifiedVehicle,
  UnifiedVehicleDetails,
  ValidAssetType,
} from '@/modules/vehicles/types';
import { IVehicleService } from './IVehicleService';

export class VehicleServiceLogDecorator implements IVehicleService {
  private readonly decoree: IVehicleService;

  public constructor(decoree: IVehicleService) {
    this.decoree = decoree;
  }

  public async loadVehicles(): Promise<void> {
    const start = this.logStart('loadVehicles');
    await this.decoree.loadVehicles();
    this.logEnd('loadVehicles', start);
  }

  public getVehicle(vehicleId: string): UnifiedVehicle {
    const start = this.logStart('getVehicle');
    const result = this.decoree.getVehicle(vehicleId);
    this.logEnd('getVehicle', start);

    return result;
  }

  public async getVehicleJSONAsset(reference: string): Promise<unknown | null> {
    const start = this.logStart('getVehicleJSONAsset');
    const result = await this.decoree.getVehicleJSONAsset(reference);
    this.logEnd('getVehicleJSONAsset', start);

    return result;
  }

  public async fetchAndUploadAssetToFileApi(
    vehicleId: string,
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    assetType: ValidAssetType,
  ): Promise<FileReferenceResponse | null> {
    const start = this.logStart('fetchAndUploadAssetToFileApi');
    const result = await this.decoree.fetchAndUploadAssetToFileApi(
      vehicleId,
      projectId,
      layoutAlternativeId,
      revisionId,
      assetType,
    );
    this.logEnd('fetchAndUploadAssetToFileApi', start);

    return result;
  }

  public async ensureVehicleAssetAvailability(
    vehicleIds: string[],
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    floorplanVehicleTypes?: VehicleType[],
  ): Promise<VehicleType[]> {
    const start = this.logStart('ensureVehicleAssetAvailability');
    const result = await this.decoree.ensureVehicleAssetAvailability(
      vehicleIds,
      projectId,
      layoutAlternativeId,
      revisionId,
      floorplanVehicleTypes,
    );
    this.logEnd('ensureVehicleAssetAvailability', start);

    return result;
  }

  public async ensureVehicleDetailsAvailability(
    vehicleIds: string[],
    floorplanVehicleTypes?: VehicleType[],
  ): Promise<void> {
    const start = this.logStart('ensureVehicleDetailsAvailability');
    await this.decoree.ensureVehicleDetailsAvailability(vehicleIds, floorplanVehicleTypes);
    this.logEnd('ensureVehicleDetailsAvailability', start);
  }

  public async reconcileEnabledVehicleTypes(
    vehicleIds: string[],
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    addNewVehiclesToHighways = false,
  ): Promise<VehicleType[]> {
    const start = this.logStart('reconcileEnabledVehicleTypes');
    const result = await this.decoree.reconcileEnabledVehicleTypes(
      vehicleIds,
      projectId,
      layoutAlternativeId,
      revisionId,
      addNewVehiclesToHighways,
    );
    this.logEnd('reconcileEnabledVehicleTypes', start);

    return result;
  }

  public cleanUpRedundantTypes(): void {
    const start = this.logStart('cleanUpRedundantTypes');
    this.decoree.cleanUpRedundantTypes();
    this.logEnd('cleanUpRedundantTypes', start);
  }

  public setVehicleDetails(vehicleDetails: UnifiedVehicleDetails[]): void {
    const start = this.logStart('setVehicleDetails');
    this.decoree.setVehicleDetails(vehicleDetails);
    this.logEnd('setVehicleDetails', start);
  }

  public removeVehicleDetails(vehicleIds: string[]): void {
    const start = this.logStart('removeVehicleDetails');
    this.decoree.removeVehicleDetails(vehicleIds);
    this.logEnd('removeVehicleDetails', start);
  }

  public async getVehicleTypeFiles(vehicleTypes: VehicleType[]): Promise<{
    uvtFiles: object[];
    uvtFileNames: string[];
    kmMdbFiles: Blob[];
    kmMdbFileNames: string[];
  }> {
    const start = this.logStart('getVehicleTypeFiles');
    const result = await this.decoree.getVehicleTypeFiles(vehicleTypes);
    this.logEnd('getVehicleTypeFiles', start);

    return result;
  }

  public async getLatestFileRef(
    assetType: ValidAssetType,
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    vehicleId: string,
  ): Promise<void> {
    const start = this.logStart('getLatestFileRef');
    await this.decoree.getLatestFileRef(
      assetType,
      projectId,
      layoutAlternativeId,
      revisionId,
      vehicleId,
    );
    this.logEnd('getLatestFileRef', start);
  }

  private logStart(methodName: string): number {
    const start = performance.now();
    logger.info(`Start -> ${methodName}`);

    return start;
  }

  private logEnd(methodName: string, start: number): void {
    const duration = performance.now() - start;
    logger.info(`End -> ${methodName} (${duration.toFixed(2)}ms)`);
  }
}
